/**
 * The swatch palette offered next to the color picker.
 *
 * Two families across the spectrum - warm hues and soft (cool) hues - each as
 * six hue columns at three lightness steps: a tint for surfaces, a tone for
 * accents and badges, a shade for text and headers.
 *
 * Generated from a hue spec rather than hand-picked hex, so the grid is
 * genuinely regular: every swatch in a row lands on the same measured
 * luminance, which is what makes warm and soft interchangeable row for row and
 * lets a theme be shifted between them without redoing the contrast work.
 * Retuning is a one-number edit here instead of thirty-six.
 *
 * Saturation is tuned per hue rather than shared: at equal saturation yellows
 * read far louder than blues, and these are mostly going to be used as surfaces.
 */

export interface Swatch {
  hex: string;
  name: string;
}

interface HueSpec {
  name: string;
  h: number;
  /** Saturation at the mid step. Tints and shades scale off this. */
  s: number;
}

/**
 * Rows are defined by TARGET LUMINANCE, not by HSL lightness.
 *
 * Equal HSL lightness does not mean equal perceived lightness: amber at L58
 * reads far brighter than red at L58, which pulled the tone row apart and left
 * the amber tint/shade pair short of AAA. Each swatch instead solves for the
 * lightness that hits its row's luminance, so a row is genuinely interchangeable
 * and every tint clears AAA against the shade beneath it.
 *
 * tint 0.90 over shade 0.045 is (0.90+0.05)/(0.045+0.05) = 10:1.
 */
const TARGET_LUMINANCE = [0.9, 0.35, 0.045] as const;
const STEP_NAMES = ['Light', '', 'Deep'] as const;

/**
 * Saturation is pushed UP at the extremes, not down.
 *
 * Hue is barely perceptible near white or near black, so scaling saturation
 * with lightness produces a row of twelve identical off-whites. Tailwind's own
 * 50-level shades sit near 100% saturation for exactly this reason. Values are
 * clamped, so the loud hues flatten at the top while the quiet ones still lift.
 */
const SAT_SCALE = [2.4, 1, 1.5] as const;
const MAX_SAT = 92;

const WARM_HUES: HueSpec[] = [
  { name: 'Sand', h: 38, s: 28 },
  { name: 'Amber', h: 45, s: 62 },
  { name: 'Orange', h: 26, s: 65 },
  { name: 'Clay', h: 14, s: 45 },
  { name: 'Red', h: 4, s: 58 },
  { name: 'Rose', h: 348, s: 42 },
];

const SOFT_HUES: HueSpec[] = [
  { name: 'Slate', h: 215, s: 14 },
  { name: 'Sky', h: 202, s: 42 },
  { name: 'Teal', h: 182, s: 32 },
  { name: 'Sage', h: 148, s: 24 },
  { name: 'Indigo', h: 232, s: 38 },
  { name: 'Violet', h: 268, s: 30 },
];

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const sat = s / 100;
  const lum = l / 100;
  const a = sat * Math.min(lum, 1 - lum);
  const channel = (n: number) => {
    const k = (n + h / 30) % 12;
    return Math.round(255 * (lum - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)));
  };
  return [channel(0), channel(8), channel(4)];
}

const toHex = (rgb: [number, number, number]) =>
  `#${rgb.map((c) => c.toString(16).padStart(2, '0')).join('')}`;

/** WCAG relative luminance, so rows can be matched on what the eye sees. */
function relativeLuminance([r, g, b]: [number, number, number]): number {
  const f = (c: number) => {
    const x = c / 255;
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

/**
 * Find the HSL lightness whose result lands on `target` luminance. Luminance
 * rises monotonically with lightness at fixed hue and saturation, so a bisection
 * converges quickly and exactly enough for 8-bit output.
 */
function solveLightness(h: number, s: number, target: number): number {
  let lo = 0;
  let hi = 100;
  for (let i = 0; i < 24; i += 1) {
    const mid = (lo + hi) / 2;
    if (relativeLuminance(hslToRgb(h, s, mid)) < target) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/** Expand a hue spec into its three steps. */
function ramp(spec: HueSpec): Swatch[] {
  return TARGET_LUMINANCE.map((target, i) => {
    const s = Math.min(MAX_SAT, spec.s * SAT_SCALE[i]);
    return {
      hex: toHex(hslToRgb(spec.h, s, solveLightness(spec.h, s, target))),
      name: [STEP_NAMES[i], spec.name].filter(Boolean).join(' '),
    };
  });
}

/**
 * Ordered by lightness row first, so the picker renders three rows of six -
 * all the tints together, then the tones, then the shades. Scanning a row is
 * how people actually choose a surface color.
 */
function grid(hues: HueSpec[]): Swatch[] {
  const ramps = hues.map(ramp);
  return TARGET_LUMINANCE.flatMap((_, step) => ramps.map((r) => r[step]));
}

export const WARM_SWATCHES: Swatch[] = grid(WARM_HUES);
export const SOFT_SWATCHES: Swatch[] = grid(SOFT_HUES);
export const SWATCHES: Swatch[] = [...WARM_SWATCHES, ...SOFT_SWATCHES];

/** Columns per row in the picker grid. */
export const SWATCH_COLUMNS = WARM_HUES.length;
